import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Globe } from 'lucide-react';
import { useState } from 'react';
import nextI18NextConfig from '../../next-i18next.config.js';
import Modal from './Modal';

const languageNames: Record<string, string> = {
  en: 'English',
  cs: 'Čeština',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pl: 'Polski',
  sk: 'Slovenčina',
};

const APP_URL = 'https://app.penetrify.cloud';

export default function Navbar() {
  const router = useRouter();
  const { t, i18n } = useTranslation('common');
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);

  const currentLocale = (router.query.locale as string) || i18n.language || 'en';
  const locales = nextI18NextConfig.i18n.locales;

  const handleLocaleChange = (newLocale: string) => {
    i18n.changeLanguage(newLocale);
    setIsLangModalOpen(false);
  };

  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '16px 0', background: 'rgba(10, 11, 13, 0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Logo */}
        <Link href={`/${currentLocale}`} style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '18px', color: 'var(--accent)', textDecoration: 'none', letterSpacing: '-0.5px' }}>
          penetrify<span style={{ color: 'var(--text-muted)' }}>.cloud</span>
        </Link>

        {/* Nav links - desktop */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }} className="hidden md:flex">
          <Link href={`/${currentLocale}#how-it-works`} style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}>
            {t('common:nav.how_it_works')}
          </Link>
          <Link href={`/${currentLocale}#under-the-hood`} style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}>
            {t('common:nav.under_the_hood')}
          </Link>
          <Link href={`/${currentLocale}#case-studies`} style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}>
            {t('common:nav.case_studies')}
          </Link>
          <Link href={`/${currentLocale}#pricing`} style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}>
            {t('common:nav.pricing')}
          </Link>
          <Link href={`/${currentLocale}/blog`} style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}>
            {t('common:nav.blog')}
          </Link>
        </div>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => setIsLangModalOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', textTransform: 'uppercase', letterSpacing: '1px' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-bright)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; }}
          >
            <Globe size={13} />
            <span>{currentLocale}</span>
          </button>
          <a
            href={`${APP_URL}/login`}
            style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '14px', fontWeight: 500, padding: '10px 16px', border: '1px solid var(--border)', borderRadius: '8px', transition: 'all 0.2s' }}
            className="hidden sm:block"
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border-bright)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-secondary)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)'; }}
          >
            {t('common:nav.login')}
          </a>
          <a
            href={APP_URL}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--accent)', color: 'var(--bg)', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, textDecoration: 'none', boxShadow: '0 0 20px var(--accent-glow)', transition: 'all 0.25s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = '#00ffaa'; (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 30px var(--accent-glow-strong)'; (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--accent)'; (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 20px var(--accent-glow)'; (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)'; }}
          >
            {t('common:nav.get_started')}
          </a>
        </div>
      </div>

      <Modal
        isOpen={isLangModalOpen}
        onClose={() => setIsLangModalOpen(false)}
        title={t('common:nav.select_language')}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {locales?.map((loc: string) => (
            <Link
              key={loc}
              href={{ pathname: router.pathname, query: { ...router.query, locale: loc } }}
              onClick={() => handleLocaleChange(loc)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', borderRadius: '8px', border: '1px solid',
                transition: 'all 0.2s', textDecoration: 'none',
                background: currentLocale === loc ? 'var(--accent)' : 'var(--bg-card)',
                borderColor: currentLocale === loc ? 'var(--accent)' : 'var(--border)',
                color: currentLocale === loc ? 'var(--bg)' : 'var(--text-secondary)',
                fontWeight: currentLocale === loc ? 600 : 400,
              }}
            >
              <span>{languageNames[loc] || loc.toUpperCase()}</span>
              {currentLocale === loc && <div style={{ width: '8px', height: '8px', background: 'var(--bg)', borderRadius: '50%' }} />}
            </Link>
          ))}
        </div>
      </Modal>
    </nav>
  );
}
